import { useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  HStack,
  Heading,
  Link,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';

import { PageContentRoot } from '#components/page-content-root';
import { GET_UPLOADS } from '#graphql/queries/uploads';
import { useAppConfig } from '#providers/app-config-provider';

/** Keys are `{userId}/{uuid}{ext}`; list view omits the user prefix. */
function resourceIdWithoutUserPrefix(resourceId: string): string {
  const i = resourceId.indexOf('/');
  return i >= 0 ? resourceId.slice(i + 1) : resourceId;
}

export function UploadsListPage() {
  const { config } = useAppConfig();
  const { data, loading, error, refetch } = useQuery(GET_UPLOADS, {
    variables: { limit: 200 },
  });

  const rows = data?.uploads ?? [];
  const apiHost = config.apiHost;

  if (loading && !data) {
    return (
      <PageContentRoot>
        <Heading size="lg" color="fg" mb={6}>
          Uploads
        </Heading>
        <Text>Loading…</Text>
      </PageContentRoot>
    );
  }

  if (error) {
    return (
      <PageContentRoot>
        <Heading size="lg" color="fg" mb={6}>
          Uploads
        </Heading>
        <Text color="red.500">Error loading uploads: {error.message}</Text>
        <Button onClick={() => refetch()} mt={4}>
          Retry
        </Button>
      </PageContentRoot>
    );
  }

  return (
    <PageContentRoot>
      <VStack gap={4} align="stretch">
        <Heading size="lg" color="fg">
          Uploads
        </Heading>
        <Text color="fg.muted" fontSize="sm">
          Files uploaded from the document editor (images and attachments).
        </Text>
        <Button size="sm" alignSelf="flex-start" onClick={() => refetch()}>
          Refresh
        </Button>
        {rows.length === 0 ? (
          <Text color="fg.muted">No uploads yet.</Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root
              size="sm"
              variant="line"
              css={{
                '& thead tr, & tbody tr': {
                  backgroundColor: 'var(--input-base-background-color)',
                },
              }}
            >
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Created</Table.ColumnHeader>
                  <Table.ColumnHeader>Filename</Table.ColumnHeader>
                  <Table.ColumnHeader>Document</Table.ColumnHeader>
                  <Table.ColumnHeader>Resource id</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="end"> </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((row) => (
                  <Table.Row key={row.id}>
                    <Table.Cell whiteSpace="nowrap">
                      {new Date(row.createdAt).toLocaleString()}
                    </Table.Cell>
                    <Table.Cell>{row.filename ?? '—'}</Table.Cell>
                    <Table.Cell fontSize="sm">
                      {row.documentPath ? (
                        <HStack gap={2} flexWrap="wrap">
                          <Link
                            asChild
                            color="blue.600"
                            _hover={{ textDecoration: 'underline' }}
                          >
                            <RouterLink to={`/documents/${row.documentPath}`}>
                              {row.documentPath}
                            </RouterLink>
                          </Link>
                          <Link
                            asChild
                            color="blue.600"
                            fontSize="xs"
                            _hover={{ textDecoration: 'underline' }}
                          >
                            <RouterLink
                              to={`/documents/${row.documentPath}/files`}
                            >
                              Files
                            </RouterLink>
                          </Link>
                        </HStack>
                      ) : (
                        '—'
                      )}
                    </Table.Cell>
                    <Table.Cell fontFamily="mono" fontSize="xs">
                      {resourceIdWithoutUserPrefix(row.resourceId)}
                    </Table.Cell>
                    <Table.Cell textAlign="end">
                      <Link
                        href={`${apiHost}/api/upload/${row.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="blue.600"
                      >
                        Open
                      </Link>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </VStack>
    </PageContentRoot>
  );
}
